import { FC, useState } from "react";
import {
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Typography,
} from "@mui/material";
import { Dayjs } from "dayjs";
import BasicDatepicker from "../../../../components/BasicDatepicker/BasicDatepicker";

export interface ReportFilterOption {
	id: string;
	name: string;
}

export interface ReportFilters {
	orgUnitId: string;
	researcherId: string;
	startDate: Dayjs | null;
	endDate: Dayjs | null;
}

export type ReportFilterKey =
	| "orgUnit"
	| "researcher"
	| "startDate"
	| "endDate";

/**
 * A filter date as the API wants it, or `undefined` when there is nothing valid
 * to send (SIMS-8895).
 *
 * The guard is the point. While the user types into a date field MUI emits an
 * onChange per keystroke, and a half-typed date arrives as an **invalid** Dayjs
 * rather than as null. Invalid Dayjs objects are truthy, so the previous
 * `date ? date.toISOString() : undefined` walked straight into
 * `RangeError: Invalid time value` and took the whole tab down with it — the
 * page needed a reload to recover.
 *
 * The invalid value still has to be stored so the field keeps showing what the
 * user is typing; it just must never reach the query.
 */
export const toApiDate = (date: Dayjs | null): string | undefined =>
	date && date.isValid() ? date.toISOString() : undefined;

interface ReportFiltersBarProps {
	idPrefix: string;
	filters: ReportFilters;
	onChange: (next: ReportFilters) => void;
	availableOrgUnits?: ReportFilterOption[];
	availableResearchers?: ReportFilterOption[];
	// Hide specific filters for tabs that don't apply them (e.g. SIMS-5801
	// Educational engagement only takes date filters per AC).
	hiddenFilters?: ReportFilterKey[];
}

// Filter row shared by every sub-report tab inside the consolidated report
// page. The four filters (Org unit + Researcher + Start date + End date)
// are identical across reports per the SIMS-5777 spec, so this component
// owns the layout and label conventions in one place.
const ReportFiltersBar: FC<ReportFiltersBarProps> = ({
	idPrefix,
	filters,
	onChange,
	availableOrgUnits = [],
	availableResearchers = [],
	hiddenFilters = [],
}) => {
	const update = (patch: Partial<ReportFilters>): void =>
		onChange({ ...filters, ...patch });
	const isHidden = (key: ReportFilterKey): boolean =>
		hiddenFilters.includes(key);

	// Which end of the range the user touched last, so the error can name the
	// field they are actually holding. Local to the bar: it is about the editing
	// gesture, not about the filter values the parent owns.
	const [lastEdited, setLastEdited] = useState<"startDate" | "endDate">(
		"endDate",
	);

	// SIMS-8895 (2): an end date before the start date was accepted silently and
	// the report simply came back empty, which reads as "no data" rather than
	// "impossible range". Guarded in two layers: the pickers bound each other so
	// the calendar cannot offer an invalid day, and the message below covers the
	// range still being invalid — the user can type a date, and clearing one end
	// then re-picking the other can leave the pair crossed.
	//
	// `isValid()` guards both ends: mid-typing MUI hands us an invalid Dayjs, and
	// `isBefore` against one of those returns false, which would silently drop the
	// error rather than raise it.
	const isRangeInvalid = Boolean(
		filters.startDate?.isValid() &&
			filters.endDate?.isValid() &&
			filters.endDate.isBefore(filters.startDate, "day"),
	);

	// The message names the field the user just moved. Both orderings describe
	// the same crossed range, but telling someone who has just set the start date
	// to "fix the end date" points them at the control they did not touch.
	const rangeError =
		lastEdited === "startDate"
			? "Start date must be on or before the end date"
			: "End date must be on or after the start date";

	return (
		<Stack spacing={0.5}>
			<Stack
				direction={{ xs: "column", md: "row" }}
				spacing={2}
				alignItems={{ xs: "stretch", md: "center" }}
			>
				{!isHidden("orgUnit") && (
					<FormControl size="small" sx={{ minWidth: 220 }}>
						<InputLabel id={`${idPrefix}-orgunit-label`}>Org unit</InputLabel>
						<Select
							labelId={`${idPrefix}-orgunit-label`}
							label="Org unit"
							value={filters.orgUnitId}
							onChange={(e) => update({ orgUnitId: e.target.value as string })}
						>
							<MenuItem value="">All org units</MenuItem>
							{availableOrgUnits.map((o) => (
								<MenuItem key={o.id} value={o.id}>
									{o.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				)}

				{!isHidden("researcher") && (
					<FormControl size="small" sx={{ minWidth: 220 }}>
						<InputLabel id={`${idPrefix}-researcher-label`}>
							Researcher
						</InputLabel>
						<Select
							labelId={`${idPrefix}-researcher-label`}
							label="Researcher"
							value={filters.researcherId}
							onChange={(e) =>
								update({ researcherId: e.target.value as string })
							}
						>
							<MenuItem value="">All researchers</MenuItem>
							{availableResearchers.map((r) => (
								<MenuItem key={r.id} value={r.id}>
									{r.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				)}

				{!isHidden("startDate") && (
					<BasicDatepicker
						label="Start date"
						value={filters.startDate}
						onChange={(v) => {
							setLastEdited("startDate");
							update({ startDate: v });
						}}
						onClear={() => {
							setLastEdited("startDate");
							update({ startDate: null });
						}}
						maxDate={filters.endDate?.isValid() ? filters.endDate : undefined}
						error={isRangeInvalid}
						sx={{ minWidth: 200, maxWidth: 220 }}
					/>
				)}

				{!isHidden("endDate") && (
					<BasicDatepicker
						label="End date"
						value={filters.endDate}
						onChange={(v) => {
							setLastEdited("endDate");
							update({ endDate: v });
						}}
						onClear={() => {
							setLastEdited("endDate");
							update({ endDate: null });
						}}
						minDate={
							filters.startDate?.isValid() ? filters.startDate : undefined
						}
						error={isRangeInvalid}
						sx={{ minWidth: 200, maxWidth: 220 }}
					/>
				)}
			</Stack>

			{/*
			 * The message sits under the whole row, not inside the End date field.
			 * As `helperText` it inherited the field's ~220px width and wrapped onto
			 * three lines, which pushed the KPI cards down. Here it has the row's
			 * full width and stays on one line, while both fields carry the red
			 * outline so the pair still reads as the thing at fault.
			 */}
			{isRangeInvalid && (
				<Typography variant="caption" color="error" role="alert">
					{rangeError}
				</Typography>
			)}
		</Stack>
	);
};

export default ReportFiltersBar;
